// Summary voyage -> angka mentah per panel. Nol import biar bisa dites pakai node.

const entriesOf = (value) => Object.entries(value || {});
const toNumber = (value) => {
  // Number(null) = 0, padahal artinya gak ada data
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};
const sum = (values) => values.reduce((total, value) => total + (toNumber(value) || 0), 0);

// sama kayak report: CENTER sendirian ditampilin MAIN
export function engineLabel(key, count) {
  return count === 1 && key === 'CENTER' ? 'MAIN' : key;
}

export function shareOf(value, total) {
  const num = toNumber(value) || 0;
  return total > 0 ? (num * 100) / total : 0;
}

// headSeas -> Head Seas
export function formatDirectionName(key) {
  const spaced = String(key).replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function buildKpis(summary) {
  const nav = summary?.navigationMetrics;
  const meMetrics = summary?.mainEngineMetrics;
  const aeFuel = entriesOf(summary?.aeMetrics?.fuelConsumption);
  const running = entriesOf(meMetrics?.totalRunningSeconds)
    .map(([name, value]) => ({ key: String(name).toUpperCase(), seconds: toNumber(value) }))
    .filter((e) => e.seconds !== null);
  return {
    distanceMeters: toNumber(nav?.totalDistance),
    averageSpeedKmh: toNumber(nav?.averageSpeed),
    meConsLiters: toNumber(meMetrics?.totalCons),
    // AE estimasi konstanta, null kalau kapal gak punya AE
    aeConsLiters: aeFuel.length ? sum(aeFuel.map(([, v]) => v)) : null,
    // running time tiap engine gak dijumlah
    engineHours: running.map((e) => ({ ...e, label: engineLabel(e.key, running.length) })),
  };
}

export function buildMainEngine(summary) {
  const metrics = summary?.mainEngineMetrics;
  const rpm = entriesOf(metrics?.averageRpm);
  const running = entriesOf(metrics?.totalRunningSeconds);
  const fuel = entriesOf(metrics?.fuelConsEngine);

  // key rpm/running huruf besar, fuel huruf kecil
  const byKey = new Map();
  const slot = (name) => {
    const key = String(name).toUpperCase();
    if (!byKey.has(key)) {
      byKey.set(key, {
        key, rpm: null, fuelLiters: null, runningSeconds: null,
      });
    }
    return byKey.get(key);
  };
  rpm.forEach(([name, value]) => { slot(name).rpm = toNumber(value); });
  running.forEach(([name, value]) => { slot(name).runningSeconds = toNumber(value); });
  fuel.forEach(([name, value]) => { slot(name).fuelLiters = toNumber(value); });

  const list = [...byKey.values()];
  const totalFuel = sum(list.map((e) => e.fuelLiters));
  const engines = list.map((e) => ({
    ...e,
    label: engineLabel(e.key, list.length),
    fuelShare: shareOf(e.fuelLiters, totalFuel),
  }));

  return {
    engines,
    hasData: engines.some((e) => (e.rpm || 0) > 0 || (e.fuelLiters || 0) > 0 || (e.runningSeconds || 0) > 0),
  };
}

export function buildAuxEngine(summary) {
  const running = summary?.aeMetrics?.runningSeconds || {};
  const fuel = summary?.aeMetrics?.fuelConsumption || {};
  const keys = [...new Set([...Object.keys(running), ...Object.keys(fuel)])];
  const totalFuel = sum(Object.values(fuel));

  const engines = keys.map((key) => {
    const runningSeconds = toNumber(running[key]) || 0;
    const fuelLiters = toNumber(fuel[key]) || 0;
    return {
      key,
      runningSeconds,
      fuelLiters,
      fuelShare: shareOf(fuelLiters, totalFuel),
    };
  });

  return {
    engines,
    hasData: engines.some((e) => e.runningSeconds > 0 || e.fuelLiters > 0),
  };
}

export function buildWeather(summary) {
  const metrics = summary?.weatherMetrics;
  const speeds = metrics?.averageSpeedDirection || {};
  const running = entriesOf(metrics?.runningSecondsDirection);
  const total = sum(running.map(([, v]) => v));

  const directions = running.map(([key, value]) => {
    const seconds = toNumber(value) || 0;
    return {
      key,
      label: formatDirectionName(key),
      seconds,
      share: shareOf(seconds, total),
      averageSpeedKmh: toNumber(speeds[key]),
    };
  });

  const waveHeight = toNumber(metrics?.averageWaveHeight);
  const wavePeriod = toNumber(metrics?.averageWavePeriod);
  // "" dari backend = gak ada sea state
  const seaState = metrics?.averageWaveHeightString || null;

  return {
    waveHeight,
    seaState,
    wavePeriod,
    directions,
    // backend kirim 0 kalau kapal gak punya device
    hasData: directions.some((d) => d.seconds > 0) || (waveHeight || 0) > 0 || (wavePeriod || 0) > 0,
  };
}

export function buildOperationMode(summary) {
  const modeKeys = [];
  const engineEntries = entriesOf(summary?.operationModeMetrics?.engine);
  const engines = engineEntries.map(([key, modesData]) => {
    const list = entriesOf(modesData);
    const total = sum(list.map(([, data]) => data?.runningSeconds));
    const modes = list.map(([modeKey, data]) => {
      if (!modeKeys.includes(modeKey)) modeKeys.push(modeKey);
      const seconds = toNumber(data?.runningSeconds) || 0;
      return { key: modeKey, seconds, share: shareOf(seconds, total) };
    });
    return { key, label: engineLabel(key, engineEntries.length), modes };
  });

  return {
    engines,
    modeKeys,
    hasData: engines.some((e) => e.modes.some((m) => m.seconds > 0)),
  };
}

// Cuma tipe yang bolong atau meternya pernah mundur yang dapat badge.
export function buildCoverageBadges(coverage, types) {
  return types.flatMap((type) => {
    const item = coverage?.[type];
    if (!item) return [];

    const warn = (item.warnings || []).includes('counter_backward');
    if (item.complete && !warn) return [];

    const parts = [type.replace(/([A-Z])/g, ' $1').toUpperCase()];
    // floor: 0.9998 jangan jadi 100%
    if (!item.complete) parts.push(`${Math.floor((item.ratio || 0) * 1000) / 10}%`);
    if (warn) parts.push('meter reset?');

    return [{ label: parts.join(' · '), warn }];
  });
}
